import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoney } from '@/lib/poTotals'
import type { PurchaseOrderDetail } from '@/types/purchaseOrders'
import { formatPoDate } from './poStatus'

/**
 * A purchase order, read-only — the current one, or an earlier revision's
 * snapshot, which has the same shape.
 */
export function PurchaseOrderView({ po }: { po: PurchaseOrderDetail }) {
  const shipTo = po.shipTo
    ? [shipToLine(po.shipTo.name), shipToLine(po.shipTo.street), [po.shipTo.postcode, po.shipTo.city].filter(Boolean).join(' '), shipToLine(po.shipTo.country), shipToLine(po.shipTo.phone), shipToLine(po.shipTo.email)].filter(Boolean)
    : []
  const terms: [string, string | null][] = [
    ['Requested delivery', po.requestedDeliveryDate ? formatPoDate(po.requestedDeliveryDate) : null],
    ['Payment terms', po.paymentTerms],
    ['Incoterms', po.incoterms],
    ['Shipping method', po.shippingMethod],
    ['Buyer', po.buyer],
    ['Reference', po.reference],
  ]

  return (
    <div className='space-y-4 text-sm'>
      <div className='grid gap-4 md:grid-cols-3'>
        <Block title='Supplier'>
          <p className='font-medium text-gray-900'>{po.supplierName}</p>
          {[po.supplierContact, po.supplierAddress, po.supplierEmail, po.supplierPhone].filter(Boolean).map((v) => (
            <p key={v} className='text-gray-600'>{v}</p>
          ))}
          {po.supplierTaxId && <p className='text-gray-600'>VAT ID {po.supplierTaxId}</p>}
        </Block>
        <Block title='Order terms'>
          <dl className='space-y-0.5'>
            {terms.map(([k, v]) => (
              <div key={k} className='flex justify-between gap-3'>
                <dt className='text-gray-500'>{k}</dt>
                <dd className='text-right text-gray-900'>{v || '—'}</dd>
              </div>
            ))}
          </dl>
        </Block>
        <Block title='Ship to'>
          {shipTo.length > 0 ? shipTo.map((v) => <p key={v} className='text-gray-600'>{v}</p>) : <p className='text-gray-400'>—</p>}
        </Block>
      </div>

      <div className='overflow-x-auto rounded-lg border'>
        <Table className='min-w-[820px]'>
          <TableHeader>
            <TableRow>
              {['#', 'Article no', 'Supplier ref', 'Description', 'Qty', 'Unit price', 'Disc. %', 'Tax %', 'Line total'].map((h) => (
                <TableHead key={h} className='whitespace-nowrap'>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {po.lines.map((l) => (
              <TableRow key={l.id}>
                <TableCell className='tabular-nums text-gray-500'>{l.lineNo}</TableCell>
                <TableCell>{l.articleNo ?? '—'}</TableCell>
                <TableCell>{l.supplierRef ?? '—'}</TableCell>
                <TableCell>{l.description ?? '—'}</TableCell>
                <TableCell className='whitespace-nowrap tabular-nums'>
                  {l.qty.toLocaleString('en-US')} {l.uom ?? ''}
                </TableCell>
                <TableCell className='tabular-nums'>{formatMoney(l.unitPrice)}</TableCell>
                <TableCell className='tabular-nums'>{l.discountPct ? `${l.discountPct}%` : '—'}</TableCell>
                <TableCell className='tabular-nums'>{`${l.taxPct ?? po.taxRatePct ?? 0}%`}</TableCell>
                <TableCell className='text-right font-medium tabular-nums'>{formatMoney(l.lineTotal)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className='flex flex-wrap justify-between gap-4'>
        <div className='max-w-md'>
          {po.notes && (
            <>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Notes</p>
              <p className='whitespace-pre-wrap text-gray-700'>{po.notes}</p>
            </>
          )}
          {po.cancelReason && (
            <p className='mt-2 text-red-600'>Cancelled: {po.cancelReason}</p>
          )}
        </div>
        <dl className='min-w-[240px] space-y-1'>
          <Row label='Subtotal' value={formatMoney(po.totals.subtotal, po.currency)} />
          {po.totals.discountTotal > 0 && <Row label='Discounts' value={`− ${formatMoney(po.totals.discountTotal, po.currency)}`} />}
          <Row label={`VAT ${po.taxRatePct ?? 0}%`} value={formatMoney(po.totals.tax, po.currency)} />
          <Row label='Shipping' value={formatMoney(po.totals.shipping, po.currency)} />
          <div className='border-t pt-1 font-semibold text-gray-900'>
            <Row label='Grand total' value={formatMoney(po.totals.grandTotal, po.currency)} />
          </div>
        </dl>
      </div>
    </div>
  )
}

const shipToLine = (v: string | null) => v ?? ''

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className='rounded-lg border border-gray-200 p-3'>
      <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500'>{title}</p>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex justify-between gap-6'>
      <dt>{label}</dt>
      <dd className='tabular-nums'>{value}</dd>
    </div>
  )
}
